import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { paymentsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../common/useCompany';
import { docAmount, makeMoneyFormatter } from '../../utils/currency';

export default function PaymentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'customer';
  const { hasPermission } = useAuth();
  const company = useCompany();
  const [payment, setPayment] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const fn = type === 'customer' ? paymentsAPI.customerGet : paymentsAPI.vendorGet;
    fn(id).then(data => {
      setPayment(data);
      setApplications(data.applications || []);
    }).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [id, type]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete payment ${payment?.payment_number}? This cannot be undone.`)) return;
    try {
      const fn = type === 'customer' ? paymentsAPI.customerDelete : paymentsAPI.vendorDelete;
      await fn(id);
      toast.success('Payment deleted');
      navigate('/payments');
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="spinner" style={{ margin: '40px auto' }} />;
  if (!payment) return <div className="empty-state"><h3>Payment not found</h3></div>;

  const base = company?.base_currency || 'USD';
  const fx = docAmount(payment, base);
  const currency = fx.currency;
  const entityName = type === 'customer' ? payment.customer_name : payment.vendor_name;
  const fmtBase = makeMoneyFormatter(fx.base);
  const fmtApp = makeMoneyFormatter(currency);

  return (
    <div>
      <div className="toolbar">
        <h2>{type === 'customer' ? 'Customer' : 'Vendor'} Payment {payment.payment_number}</h2>
        <div className="toolbar-right">
          <span className={`badge badge-${payment.status?.toLowerCase()}`} style={{ fontSize: 13, padding: '6px 14px' }}>{payment.status}</span>
          {payment.status !== 'Voided' && hasPermission('payments:create') && (
            <button className="btn btn-outline" onClick={() => navigate(`/payments/${id}/edit?type=${type}`)}>Edit</button>
          )}
          {payment.status !== 'Voided' && (hasPermission('payments:void') || hasPermission('payments:create')) && (
            <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
          )}
          <button className="btn btn-outline" onClick={() => navigate('/payments')}>Back</button>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 820, margin: '0 auto' }}>
        <div className="card-header"><h3>Payment Details</h3></div>
        <div className="card-body">
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="form-group">
              <label>{type === 'customer' ? 'Customer' : 'Vendor'}</label>
              <div className="stat-change text-muted" style={{ fontSize: 14 }}>{entityName}</div>
            </div>
            <div className="form-group">
              <label>Payment Date</label>
              <div className="stat-change text-muted" style={{ fontSize: 14 }}>{payment.payment_date}</div>
            </div>
            <div className="form-group">
              <label>Payment Method</label>
              <div className="stat-change text-muted" style={{ fontSize: 14 }}>{payment.payment_method}</div>
            </div>
            <div className="form-group">
              <label>Reference</label>
              <div className="stat-change text-muted" style={{ fontSize: 14 }}>{payment.reference || '-'}</div>
            </div>
            <div className="form-group">
              <label>Currency</label>
              <div className="stat-change text-muted" style={{ fontSize: 14 }}>{currency}</div>
            </div>
            <div className="form-group">
              <label>Status</label>
              <div className="stat-change text-muted" style={{ fontSize: 14 }}>{payment.status}</div>
            </div>
          </div>

          <div style={{ marginTop: 20, borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
            <h4 style={{ 'margin': '0 0 14px', fontSize: 13, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '.3px' }}>
              {fx.isForeign ? 'Currency Exchange' : 'Amount'}
            </h4>
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <div className="stat-card">
                <div className="stat-label">Amount ({currency})</div>
                <div className="stat-value" style={{ fontSize: 20 }}>{fmtApp(fx.amount)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Exchange Rate</div>
                <div className="stat-value" style={{ fontSize: 20 }}>
                  1 {currency} = {fx.rate.toLocaleString('en-US', { maximumFractionDigits: 4 })} {fx.base}
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">{fx.base} Equivalent</div>
                <div className="stat-value" style={{ fontSize: 20 }}>{fmtBase(fx.baseAmount)}</div>
              </div>
            </div>
            {fx.isForeign && (
              <div style={{ marginTop: 10, fontSize: 13, color: '#0f766e' }}>
                {fmtApp(fx.amount)} = <strong>{fmtBase(fx.baseAmount)}</strong>
              </div>
            )}
            {parseFloat(payment.unapplied_amount) > 0 && (
              <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
                Unapplied: <strong>{fmtApp(payment.unapplied_amount)}</strong>
              </div>
            )}
          </div>

          {payment.memo && (
            <div style={{ marginTop: 20 }}>
              <label>Memo</label>
              <div className="stat-change text-muted" style={{ fontSize: 14 }}>{payment.memo}</div>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, maxWidth: 820, marginInline: 'auto' }}>
        <div className="card-header"><h3>Applied to {type === 'customer' ? 'Invoices' : 'Bills'}</h3></div>
        <div className="card-body" style={{ padding: 0 }}>
          {applications.length === 0 ? (
            <div className="empty-state"><h3>No applications</h3></div>
          ) : (
          <table>
            <thead>
              <tr>
                <th>{type === 'customer' ? 'Invoice' : 'Bill'} #</th>
                <th className="text-right">Amount Applied ({currency})</th>
              </tr>
            </thead>
            <tbody>
              {applications.map(a => (
                <tr key={a.id}>
                  <td>{a.invoice_number || a.bill_number}</td>
                  <td className="amount">{fmtApp(a.amount_applied)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </div>
    </div>
  );
}